import { Fragment, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Bell, X, ChevronDown, Baby, Home } from 'lucide-react';
import { nexaApi } from './api';
import { RoutineChecklist } from './RoutineChecklist';
import { SupplementsCard } from './SupplementsCard';
import { CalendarCard } from './CalendarCard';
import { MorningCheckin } from './MorningCheckin';
import { Commitments } from './Commitments';
import { ActiveSigns } from './ActiveSigns';
import { custodyFor, nextTransition, PARENT_LABEL } from './custody';

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning, Lev';
  if (h < 18) return 'Good afternoon, Lev';
  return 'Good evening, Lev';
};

export const TodayView = () => {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('nexa_routine_collapsed') || '{}');
    } catch {
      return {};
    }
  });

  const isOpen = (r) => {
    if (collapsed[r.id] !== undefined) return !collapsed[r.id];
    const h = new Date().getHours();
    if (r.id === 'evening') return h >= 18;
    return h < 14; // morning open until early afternoon by default
  };

  const toggleOpen = (r) => {
    setCollapsed((prev) => {
      const next = { ...prev, [r.id]: isOpen(r) };
      localStorage.setItem('nexa_routine_collapsed', JSON.stringify(next));
      return next;
    });
  };

  const load = async () => {
    const { data: d } = await nexaApi.get('/today');
    setData(d);
  };

  useEffect(() => {
    load();
    const refresh = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const toggle = async (routineId, item) => {
    setData((prev) => ({
      ...prev,
      routines: prev.routines.map((r) =>
        r.id !== routineId
          ? r
          : {
              ...r,
              items: r.items.map((i) => (i.id === item.id ? { ...i, done: !i.done } : i)),
              completed: r.completed + (item.done ? -1 : 1),
            },
      ),
    }));
    try {
      await nexaApi.post('/routine/check', { item_id: item.id, done: !item.done, routine_id: routineId });
    } catch {
      toast.error('Could not save — check connection');
      load();
    }
  };

  const toggleSupp = async (item) => {
    setData((prev) => ({
      ...prev,
      supplements: {
        ...prev.supplements,
        items: prev.supplements.items.map((i) => (i.id === item.id ? { ...i, done: !i.done } : i)),
        completed: prev.supplements.completed + (item.done ? -1 : 1),
      },
    }));
    try {
      await nexaApi.post('/routine/check', { item_id: item.id, done: !item.done, routine_id: 'supplements' });
    } catch {
      toast.error('Could not save — check connection');
      load();
    }
  };

  const reorder = async (routineId, newItems) => {
    setData((prev) => ({
      ...prev,
      routines: prev.routines.map((r) => (r.id === routineId ? { ...r, items: newItems } : r)),
    }));
    try {
      await nexaApi.post('/routine/reorder', {
        routine_id: routineId,
        item_ids: newItems.map((i) => i.id),
      });
    } catch {
      toast.error('Could not save order');
      load();
    }
  };

  const setDayOff = async (routineId, dayOff) => {
    setData((prev) => ({
      ...prev,
      routines: prev.routines.map((r) => (r.id === routineId ? { ...r, day_off: dayOff } : r)),
    }));
    try {
      await nexaApi.post(`/routine/${routineId}/day-off`, { day_off: dayOff });
    } catch {
      toast.error('Could not update day off');
      load();
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
      </div>
    );
  }

  const pct = (r) => (r.total ? Math.round((r.completed / r.total) * 100) : 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="lg:max-w-[1600px] lg:mx-auto lg:px-10 xl:px-14 lg:py-10"
      data-testid="nexa-today-view"
    >
      <div className="mb-6 lg:mb-10">
        <h1 className="nexa-heading text-3xl lg:text-5xl font-light tracking-tight">{greeting()}</h1>
        <p className="text-white/40 text-sm mt-1">
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <KidsDayBadge />
      </div>

      <div className="hidden lg:block mb-8">
        <ActiveSigns />
      </div>

      <div className="flex flex-col gap-6 lg:block lg:columns-2 xl:columns-3 lg:gap-8 lg:[&>*]:mb-8 lg:[&>*]:break-inside-avoid">
      <Commitments />

      <MorningCheckin data={data} onSaved={load} />

      <CalendarCard />

      {data.reminders && data.reminders.length > 0 && (
        <div data-testid="nexa-reminders-card">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.8} />
            <span className="text-xs tracking-[0.2em] uppercase text-white/40">Reminders</span>
          </div>
          <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden">
            {data.reminders.map((r, idx) => (
              <div
                key={r.id}
                className={`flex items-center gap-3 px-5 py-3.5 ${
                  idx < data.reminders.length - 1 ? 'border-b border-white/[0.06]' : ''
                }`}
                data-testid={`nexa-reminder-${r.id}`}
              >
                <span className="text-sky-400 font-mono text-sm w-14 shrink-0">{r.time}</span>
                <span className="flex-1 text-sm text-white/85">{r.text}</span>
                <span className="text-[10px] uppercase tracking-wider">
                  {r.repeat === 'weekly' && r.weekday != null
                    ? <span className="text-sky-300/90 font-medium">{['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'][r.weekday]}</span>
                    : <span className="text-white/35">{r.repeat}</span>}
                </span>
                <button
                  onClick={async () => {
                    try {
                      await nexaApi.delete(`/reminders/${r.id}`);
                      load();
                    } catch {
                      toast.error('Could not remove');
                    }
                  }}
                  data-testid={`nexa-reminder-remove-${r.id}`}
                  aria-label="Remove reminder"
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white/80 active:scale-90 transition-all"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-white/30 mt-2 px-1">
            Tell NEXA in chat to add or remove reminders — &ldquo;remind me at 21:00 to stretch&rdquo;.
          </p>
        </div>
      )}

      {data.routines.map((r) => {
        const open = isOpen(r);
        return (
          <Fragment key={r.id}>
          <div data-testid={`nexa-routine-${r.id}`}>
            <button
              type="button"
              onClick={() => toggleOpen(r)}
              data-testid={`nexa-routine-toggle-${r.id}`}
              aria-expanded={open}
              className="w-full flex items-center justify-between mb-3 text-left"
            >
              <span className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/60">
                <motion.span
                  animate={{ rotate: open ? 0 : -90 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                  className="inline-flex text-sky-400"
                >
                  <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
                </motion.span>
                {r.time} — {r.name}
              </span>
              <span
                className="text-sm text-sky-400 font-semibold"
                data-testid={`nexa-routine-progress-${r.id}`}
              >
                {r.completed}/{r.total}
              </span>
            </button>
            {r.is_work && (
              <button
                type="button"
                onClick={() => setDayOff(r.id, !r.day_off)}
                data-testid={`nexa-routine-dayoff-${r.id}`}
                className={`mb-3 -mt-1 text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                  r.day_off
                    ? 'border-amber-400/50 bg-amber-400/15 text-amber-200'
                    : 'border-white/15 text-white/45 hover:text-white/80 hover:border-white/30'
                }`}
              >
                {r.day_off ? '🌴 Day off — tap to bring it back' : 'Day off'}
              </button>
            )}
            {r.day_off ? (
              <div className="text-sm text-white/40 mb-4 pl-1">Enjoy your day off — the checklist is hidden for today.</div>
            ) : (
            <>
            <div className="h-1 rounded-full bg-white/[0.06] mb-4 overflow-hidden">
              <motion.div
                animate={{ width: `${pct(r)}%` }}
                transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                className="h-full bg-sky-400 rounded-full"
              />
            </div>
            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  key="body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="overflow-hidden"
                >
                  <RoutineChecklist
                    items={r.items}
                    prefix={r.id}
                    onToggle={(item) => toggle(r.id, item)}
                    onReorder={(newItems) => reorder(r.id, newItems)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            </>
            )}
          </div>
          {r.id === 'morning' && (
            <SupplementsCard supplements={data.supplements} onToggle={toggleSupp} />
          )}
          </Fragment>
        );
      })}
      </div>

    </motion.div>
  );
};

/* --------------------------- Kids day badge --------------------------- */
const KidsDayBadge = () => {
  const parent = custodyFor(new Date());
  const nxt = nextTransition(new Date());
  const isMine = parent === 'me';
  const kidsToday = isMine;

  const daysCopy = (() => {
    if (!nxt) return null;
    if (nxt.daysUntil === 1) return 'tomorrow';
    if (nxt.daysUntil === 2) return 'in 2 days';
    return `in ${nxt.daysUntil} days`;
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      data-testid="nexa-today-kids-badge"
      className={`mt-3 inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border text-[11px] tracking-[0.18em] uppercase font-mono ${
        kidsToday
          ? 'border-sky-400/45 bg-sky-400/[0.08] text-sky-200'
          : 'border-white/12 bg-white/[0.03] text-white/60'
      }`}
    >
      <span
        className={`w-5 h-5 rounded-full grid place-items-center ${
          kidsToday ? 'bg-sky-400/20 text-sky-300' : 'bg-white/8 text-white/50'
        }`}
      >
        {kidsToday ? <Baby className="w-3 h-3" strokeWidth={2} /> : <Home className="w-3 h-3" strokeWidth={2} />}
      </span>
      <span>
        {kidsToday
          ? `Kids day · with you${daysCopy ? ` — Nigar's ${daysCopy}` : ''}`
          : `With ${PARENT_LABEL.nigar}${daysCopy ? ` — kids ${daysCopy}` : ''}`}
      </span>
    </motion.div>
  );
};
